import styles from "./CodeBlock.module.css";
import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Normalise the lang strings that come from lesson JSON to Prism identifiers
const LANG_MAP: Record<string, string> = {
    'c++':      'cpp',
    'c#':       'csharp',
    'c-sharp':  'csharp',
    'kotlin':   'kotlin',
    'shell':    'bash',
    'sh':       'bash',
};

// Languages whose comment char is # instead of //
const HASH_COMMENT_LANGS = new Set(['python', 'bash', 'shell', 'sh', 'yaml', 'toml', 'ruby', 'perl']);

function getCommentChar(lang: string): string {
    return HASH_COMMENT_LANGS.has(lang?.toLowerCase()) ? '#' : '//';
}

/**
 * Returns true when ≥ 85% of non-blank lines are comment lines.
 * These blocks are reference/config text disguised as code and look
 * terrible in syntax highlighting (everything turns dim grey).
 */
function isCommentHeavy(snippet: string, commentChar: string): boolean {
    const lines = snippet.trim().split('\n').filter(l => l.trim().length > 0);
    if (lines.length < 3) return false;
    const commentCount = lines.filter(l => l.trim().startsWith(commentChar)).length;
    return commentCount / lines.length >= 0.85;
}

/** Strip leading comment chars and return the cleaned lines. */
function stripComments(snippet: string, commentChar: string): string[] {
    return snippet.trim().split('\n').map(l => {
        const trimmed = l.trimEnd();
        if (trimmed.trim().startsWith(commentChar)) {
            // Remove the comment char (and one optional space after it)
            return trimmed.replace(/^\s*/, '').slice(commentChar.length).replace(/^ /, '');
        }
        return trimmed;
    });
}

function CopyButton({ snippet }: { snippet: string }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(snippet).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <button className={styles.copyBtn} onClick={handleCopy}>
            {copied ? (
                <>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M2 6.5L5 9.5L11 3.5" stroke="currentColor" strokeWidth="1.6"
                              strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Copied
                </>
            ) : (
                <>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <rect x="4.5" y="1.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                        <path d="M1.5 4.5H3V11.5H10V10" stroke="currentColor" strokeWidth="1.3"
                              strokeLinecap="round"/>
                    </svg>
                    Copy
                </>
            )}
        </button>
    );
}

function CodeBlock({ lang, snippet }) {
    const prismLang    = LANG_MAP[lang?.toLowerCase()] ?? lang?.toLowerCase() ?? 'text';
    const commentChar  = getCommentChar(lang);
    const commentHeavy = isCommentHeavy(snippet, commentChar);

    const header = (
        <div className={styles.codeHeader}>
            <span className={styles.codeLang}>{lang}</span>
            <CopyButton snippet={snippet} />
        </div>
    );

    // ── Comment-heavy blocks: render as a readable reference panel ──
    if (commentHeavy) {
        const lines = stripComments(snippet, commentChar);
        return (
            <div className={styles.codeBlock}>
                {header}
                <div className={styles.referenceBody}>
                    {lines.map((line, i) =>
                        line.trim() === ''
                            ? <div key={i} className={styles.referenceSpacer} />
                            : <div key={i} className={styles.referenceLine}>{line}</div>
                    )}
                </div>
            </div>
        );
    }

    // ── Normal code: full syntax highlighting ──
    return (
        <div className={styles.codeBlock}>
            {header}
            <SyntaxHighlighter
                language={prismLang}
                style={oneLight}
                customStyle={{
                    margin: 0,
                    padding: '18px 20px',
                    background: '#f6f8fa',
                    fontSize: '13.5px',
                    lineHeight: '1.7',
                    borderRadius: 0,
                    fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
                }}
                wrapLongLines={false}
            >
                {snippet}
            </SyntaxHighlighter>
        </div>
    );
}

export default CodeBlock;